import {
    Announced,
    Checkbox, CheckboxVisibility, ChoiceGroup, DetailsList, Dropdown, IChoiceGroupOption, IColumn, IDropdownOption, ITextFieldStyles, Link,
    mergeStyles,
    ProgressIndicator, ScrollablePane,
    Spinner, SpinnerSize, TextField, TooltipHost
} from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import { useId } from "@fluentui/react-hooks";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import React, { Dispatch, FormEvent, SetStateAction, useState } from "react";
import { CSVLink } from "react-csv";
import { CSVReader } from "react-papaparse";
import { RenderStickyHeader } from "../../Helpers/StickyHeader";
import { IOnboardingDataType } from "../../Pages/OnboardingPage";
import { getOnboardedServices } from "../../services/ApiService/Requests";
import { DataActionEnum } from "../../services/DataContext/DataActions";
import HttpService from "../../services/HttpService/HttpService";
import { breakWord, calloutProps, hostStyles } from "../../Styles/Page.styles";
import { useAppInsights } from "../AppInsights/AppInsights";
import { trackEvent, trackException } from "../AppInsights/LoggingHelper";
import { IJobInfo } from "../ManageServers/DetailsListManageServers";
import { linuxOnboardingStepsText, UnauthorizedMessage } from "../Shared/AppConstants";
import { RootContext } from "../Stores/RootStore";
import { AddServersToJobsPanel, AddServersToJobsPanelProps, AddServersToJobsPanelType } from "./AddServersToJobsPanel";
import { IServiceDataType } from "../../App";
import { Status } from "../../Styles/Colors";
import { PeoplePicker, PeoplePickerRequestType } from "../Shared/PeoplePicker";
import { fetchGraphAPIAuthToken } from "../../services/FetchGraphAPIAuthToken";
import { LinuxLogo32Icon } from '@fluentui/react-icons-mdl2';
import { initializeIcons } from '@fluentui/font-icons-mdl2';
import { Icon } from '@fluentui/react/lib/Icon';
initializeIcons();

// Interfaces //

export enum OnboardingStep {
    Prerequisites,
    ARC,
    Upload,
    Onboard,
}

export enum SubmissionStatus {
    NONE,
    PENDING,
    SUCCESS,
    FAILED,
}

interface IPrerequisitesComponentProps {
    SetCurrentStep: Dispatch<SetStateAction<OnboardingStep>>;
}

interface IARCComponentProps {
    SetCurrentStep: Dispatch<SetStateAction<OnboardingStep>>;
}

interface IUploadComponentProps {
    SetCurrentStep: Dispatch<SetStateAction<OnboardingStep>>;
    SetServerData: Dispatch<SetStateAction<IOnboardingDataType[]>>;
    SetOverwriteServicetree: Dispatch<SetStateAction<boolean>>;
}

interface IOnboardComponentProps {
    serverData: IOnboardingDataType[];
    SetCurrentStep: Dispatch<SetStateAction<OnboardingStep>>;
    overwriteServicetree: boolean;
}

export interface IFQDNValidation {
    Valid: boolean;
    Server: string;
    Domain: string;
    Error: string;
}

const textFieldStyles: Partial<ITextFieldStyles> = { root: { maxWidth: "450px" } };

// Consts //

const onboardingStatusText: string = "Onboarding Review";
const arcText: string = "Do you have ARC installed on any of these machines you plan to onboard?";
const azureIaasText: string = "Are you onboarding any Azure IaaS VMs?";
const firstTimeText: string = "Is this the first time you have enlisted this subscription/resource group combination to the Microsoft Digital Patching Service?";
const provideSubIdText: string = "Please provide your Azure subscription name OR subscription Id";
const resourceGroupNameText: string = "Please provide your Azure resource group name";
const peoplePickerText: string = "Contact Alias (must have Owner permission on the Azure subscription)";
const arcSuccessText: string = "Thank you, please continue to onboard your machines";
const arcFailureText: string = "Unexpected failure during submission, please refresh page and try again.";
const vmAcknowledgement: string = "I acknowledge that I have followed the steps in the wiki above and my VMs are configured appropriately.";
const iaasConfigurationHelp: string = "Azure IaaS Configuration Help";
const iaasLink: string = "https://aka.ms/mdpatchingIaaS";

export const PrerequisitesComponent: React.FunctionComponent<IPrerequisitesComponentProps> = (props: IPrerequisitesComponentProps) => {

    const [dataLoaded, SetDataLoaded] = React.useState<boolean>(false);
    const { state } = React.useContext(RootContext);
    const [confirmCheck, SetConfirmCheck] = React.useState<boolean>(true);
    const [selectedOS, SetSelectedOS] = React.useState<string>("windows");

    const appInsights = useAppInsights();

    // HttpService
    const [httpService] = React.useState(HttpService(appInsights, state));

    // React.useEffect(() => {
    //     if (state.AuthStore.Token !== "") {
    //     }
    // }, [state.AuthStore.Token]);

    function _onChecked(ev?: React.FormEvent<HTMLElement>, isChecked?: boolean) {
        SetConfirmCheck(!isChecked);
    }

    const options: IChoiceGroupOption[] = [
        {
            key: 'windows', text: 'Windows  ', onRenderField: (props, render) => {
                return (
                    <>
                        <Icon iconName="WindowsLogo" style={{ fontSize: '2em' }} />
                        {render!(props)}
                    </>
                );
            },
            defaultChecked: true
        },
        {
            key: 'linux', text: 'Linux  ',
            onRenderField: (props, render) => {
                return (
                    <>
                        <LinuxLogo32Icon style={{ fontSize: '2em' }} />
                        {render!(props)}
                    </>
                );
            }
        },
    ];

    const _onChange = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption): void => {
        SetSelectedOS(option?.key || "windows");
    }

    return (
        <div>
            <ChoiceGroup label="Which operating system are you using?" defaultSelectedKey="windows" options={options} onChange={_onChange} />
            <h2 className={breakWord}>Prerequisites for Onboarding</h2>
            <span className={breakWord}>
                <ul>
                    <li>Machine must be present on <a href="https://aka.ms/mlspatch"> MLS Patching Power BI dashboard</a></li>
                    <li>Machine computer objects must reside under the MLS OU in Active Directory</li>
                    <li>Each onboarded machine must coincide with a patching job</li>
                    <li>5+ GB of free space maintained on the OS drive</li>
                    <li>Windows Server 2016+ & Windows 11 [for Windows OS’s]</li>
                </ul>
            </span>
            {selectedOS === "linux" &&
                <span className={breakWord}>{linuxOnboardingStepsText}</span>
            }
            <br /><br />
            <Checkbox
                id="prerequisitesCheckBox"
                label="Please check to confirm you have read these conditions"
                ariaLabel="Please check to confirm you have read these conditions"
                boxSide="end"
                onChange={_onChecked}
            />
            <br />
            <span>
                <Button
                    id="continueButton"
                    role="button"
                    appearance="primary"
                    aria-label="Continue Button"
                    disabled={confirmCheck}
                    onClick={() => props.SetCurrentStep(OnboardingStep.Upload)}
                >Continue</Button>
            </span>
        </div>
    );
};

export const ARCComponent: React.FunctionComponent<IARCComponentProps> = (props: IARCComponentProps) => {
    const { state } = React.useContext(RootContext);
    const [arcConfirm, SetArcConfirm] = React.useState<string | undefined>();
    const [iaasConfirm, SetIaasConfirm] = React.useState<string | undefined>();
    const [firstTimeConfirm, SetFirstTimeConfirm] = React.useState<string | undefined>();
    const [subscriptionNameOrId, SetSubscriptionNameOrId] = React.useState<string>("");
    const [resourceGroupName, SetResourceGroupName] = React.useState<string>("");
    const [contactAlias, SetContactAlias] = React.useState<string>("");
    const [arcSubmissionStatus, SetArcSubmissionStatus] = React.useState<SubmissionStatus>(SubmissionStatus.NONE);
    const [resultMessage, SetResultMessage] = React.useState<string>(arcSuccessText);
    const [graphToken, SetGraphToken] = React.useState<string>("");
    const [vmAcknowledged, SetVMAcknowledged] = React.useState<boolean>(false);

    const appInsights = useAppInsights();
    const tooltipId = useId("tooltip");

    // HttpService
    const [httpService] = React.useState(HttpService(appInsights, state));

    React.useEffect(() => {
        if (graphToken === "") {
            fetchGraphAPIAuthToken(state.AuthStore.Account).then((graphAuthResponse: any) => {
                SetGraphToken(graphAuthResponse);
            }).catch((reason: any) => {
                trackException(appInsights, reason, SeverityLevel.Error, "ServerAdministration", "fetchGraphAPIAuthToken", "Necessary for PeoplePicker", state.AuthStore, {});
            });
        }
    }, []);

    const onArcConfirm = (event: FormEvent<HTMLDivElement>, option?: IDropdownOption<any> | undefined, index?: number | undefined) => {
        SetArcConfirm(option?.text);
    }

    const onIaaSConfirm = (event: FormEvent<HTMLDivElement>, option?: IDropdownOption<any> | undefined, index?: number | undefined) => {
        SetIaasConfirm(option?.text);

        if (option?.text === "No") {
            SetVMAcknowledged(false);
        }
    }

    const onFirstTimeConfirm = (event: FormEvent<HTMLDivElement>, option?: IDropdownOption<any> | undefined, index?: number | undefined) => {
        SetFirstTimeConfirm(option?.text);
    }

    const SubmitArcQuestions = () => {
        SetArcSubmissionStatus(SubmissionStatus.PENDING);

        httpService.post({
            url: "api/IcM/onboardingArcRequest",
            token: state.AuthStore.Token,
            data: {
                subscriptionNameOrId: subscriptionNameOrId,
                resourceGroupName: resourceGroupName,
                contactAlias: contactAlias,
                azureIaas: iaasConfirm === "Yes",
            },
        })
            .then((resp: any) => {
                console.log(resp);

                if (resp?.data.error == null) {
                    // success
                    SetResultMessage(arcSuccessText);
                    SetArcSubmissionStatus(SubmissionStatus.SUCCESS);
                } else {
                    // error
                    SetResultMessage(resp?.data?.error?.message);
                    SetArcSubmissionStatus(SubmissionStatus.FAILED);
                }
            }).catch(err => {
                console.log(err);

                SetResultMessage(arcFailureText);
                SetArcSubmissionStatus(SubmissionStatus.FAILED);
            });
    }

    const toolTipContent = (): string => {
        if (allFieldsPopulated()) {
            return "Submit";
        }

        const message: string[] = [];

        if (subscriptionNameOrId === "") {
            message.push("Subscription Name/Id is blank");
        }

        if (resourceGroupName === "") {
            message.push("Resource Group Name is blank");
        }

        if (contactAlias === "") {
            message.push("Contact Alias is blank");
        }

        return message.join(", ");
    }

    const onChangeSubscriptionNameOrId = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        SetSubscriptionNameOrId(newValue!);
    }
    const onChangeReourceName = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        SetResourceGroupName(newValue!);
    }

    const allFieldsPopulated = (): boolean => {
        return subscriptionNameOrId !== "" && resourceGroupName !== ""
            && contactAlias !== "" && arcConfirm !== "" && firstTimeConfirm !== "";
    }

    const onVMAcknowledged = (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, checked?: boolean) => {
        SetVMAcknowledged(checked!);
    }

    const disableInput = (): boolean => {
        if (iaasConfirm === "Yes" && !vmAcknowledged) {
            return true;
        }

        return !firstTimeConfirm || firstTimeConfirm === "No" || (iaasConfirm === "No" && arcConfirm === "No");
    }

    const disableSubmitButton = (): boolean => {
        return disableInput() || !allFieldsPopulated();
    }

    const disableContinueButton = (): boolean => {
        const isFirstDropdownEnabled = !disableFirstTimeDropdown();

        if (isFirstDropdownEnabled) {
            if (firstTimeConfirm === "No") {
                return false;
            }

            return !allFieldsPopulated() || arcSubmissionStatus !== SubmissionStatus.SUCCESS;
        }

        if (iaasConfirm === "Yes" && vmAcknowledged && firstTimeConfirm === "No") {
            return false;
        }

        return !(arcConfirm == "No" && iaasConfirm == "No");
    }

    const disableFirstTimeDropdown = (): boolean => {
        if (arcConfirm == undefined || iaasConfirm == undefined) {
            return true;
        }

        return ((!arcConfirm || arcConfirm === "No")
            && (!iaasConfirm || iaasConfirm === "No"))
            || (iaasConfirm === "Yes" && !vmAcknowledged);
    }

    return (
        <>
            <>
                <h2 className={breakWord}>Azure ARC / IaaS</h2>
                <br />
                <Dropdown
                    label={arcText}
                    options={["Yes", "No"].map((item) => ({ key: item, text: item }))}
                    onChange={onArcConfirm}
                    styles={textFieldStyles}
                />
                <Dropdown
                    label={azureIaasText}
                    options={["Yes", "No"].map((item) => ({ key: item, text: item }))}
                    onChange={onIaaSConfirm}
                    styles={textFieldStyles}
                />
                {iaasConfirm == "Yes" &&
                    <>
                        <br />
                        <Link
                            href={iaasLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={iaasConfigurationHelp}
                            name={iaasConfigurationHelp}
                            aria-describedby={iaasConfigurationHelp}
                            styles={{ root: { textDecoration: 'underline' } }}>
                            {iaasConfigurationHelp}
                        </Link>
                        <Checkbox
                            className={mergeStyles({ marginTop: "10px", maxWidth: "450px" })}
                            label={vmAcknowledgement}
                            ariaLabel={vmAcknowledgement}
                            // aria-describedby={tooltipId}
                            aria-required="false"
                            defaultChecked={false}
                            onChange={onVMAcknowledged}
                        />
                        <br />
                    </>
                }
                <Dropdown
                    label={firstTimeText}
                    options={["Yes", "No"].map((item) => ({ key: item, text: item }))}
                    onChange={onFirstTimeConfirm}
                    styles={textFieldStyles}
                    disabled={disableFirstTimeDropdown()}
                />
                <TextField
                    id="subscriptionName"
                    label={provideSubIdText}
                    resizable={false}
                    value={subscriptionNameOrId}
                    autoAdjustHeight
                    required
                    ariaLabel={provideSubIdText}
                    onChange={onChangeSubscriptionNameOrId}
                    styles={textFieldStyles}
                    disabled={disableInput()}
                />
                <TextField
                    id="resourceName"
                    label={resourceGroupNameText}
                    resizable={false}
                    value={resourceGroupName}
                    autoAdjustHeight
                    required
                    ariaLabel={resourceGroupNameText}
                    onChange={onChangeReourceName}
                    styles={textFieldStyles}
                    disabled={disableInput()}
                />
                <PeoplePicker
                    token={graphToken}
                    account={state.AuthStore.Account}
                    defaultValue={""}
                    alias={contactAlias}
                    itemLimit={1}
                    required={true}
                    SetAlias={SetContactAlias}
                    SetAliasInput={() => { }}
                    SetNewEmail={() => { }}
                    PeoplePickerRequestTypeValue={PeoplePickerRequestType.Alias}
                    label={peoplePickerText}
                    ariaLabel={peoplePickerText}
                    styles={textFieldStyles}
                    disabled={disableInput()}
                />
                <br />
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <TooltipHost
                        content={toolTipContent()}
                        id={tooltipId}
                        calloutProps={calloutProps}
                    >
                        <Button
                            id="submitButton"
                            key="submitButton"
                            role="button"
                            aria-label="Submit Button"
                            disabled={disableSubmitButton()}
                            onClick={SubmitArcQuestions}
                        >Submit</Button>
                    </TooltipHost>
                    {arcSubmissionStatus == SubmissionStatus.PENDING && (
                        <Spinner label="Submitting request..." ariaLive="assertive" labelPosition="left" />
                    )}
                </div>
                {arcSubmissionStatus == SubmissionStatus.SUCCESS && <span style={{ color: Status.DarkGreen }}>{resultMessage}</span>}
                {arcSubmissionStatus == SubmissionStatus.FAILED && <span style={{ color: Status.Red }}>{resultMessage}</span>}
                <br />
                <br />
            </>
            <Button
                id="continueButton"
                role="button"
                appearance="primary"
                aria-label="Continue Button"
                disabled={disableContinueButton()}
                onClick={() => props.SetCurrentStep(OnboardingStep.Upload)}
            >Continue</Button>
        </>
    );
};

export const UploadDataComponent: React.FunctionComponent<IUploadComponentProps> = (props: IUploadComponentProps) => {
    const [dataGridData, SetDataGridData] = React.useState<IOnboardingDataType[]>([]);
    const [hideResults, SetHideResults] = React.useState<boolean>(true);
    const [maxOnboardingCount, SetMaxOnboardingCount] = React.useState<number>(1000);
    const [hideOverLimitMessage, SetHideOverLimitMessage] = React.useState<boolean>(true);
    const [showIncorrectFileTypeMessage, SetShowIncorrectFileType] = React.useState<boolean>(false);
    const [columnIsSortedDescending, SetColumnIsSortedDescending] = React.useState<boolean>(false);
    const [showUploadCSVDataAlert, SetShowUploadCSVDataAlert] = React.useState<boolean>(false);
    const [descendingColumn, SetDescendingColumn] = useState<string>("Status");
    const [sortedColumn, SetSortedColumn] = useState<string>("Status");
    const [overrideServersOU, SetOverrideServersOU] = React.useState<boolean>(false);
    const { state } = React.useContext(RootContext);

    const [announceCSVUpload, SetAnnounceCSVUpload] = React.useState<JSX.Element | undefined>(undefined);

    const appInsights = useAppInsights();

    // HttpService
    const [httpService] = React.useState(HttpService(appInsights, state));

    const csvFileName = "servers.csv";
    const csvHeaders = [{ label: "FQDN", key: "FQDN" }];
    const csvData = [{ FQDN: "" }];

    const tooltipId = useId("tooltip");

    React.useEffect(() => {
        const fetchMaxOnboardingCount = httpService.get({
            url: "api/ServerOnboarding/maxOnboardingCount",
            token: state.AuthStore.Token,
            params: {},
        });

        fetchMaxOnboardingCount.then((response: any) => {
            const data: any = response?.data;
            SetMaxOnboardingCount(data || 0);
        }).catch((reason: any) => {
            trackException(appInsights, reason, SeverityLevel.Error, "Onboarding", "Upload Data", "fetchMaxOnboardingCount", state.AuthStore, {});
        });

        SetShowUploadCSVDataAlert(false);
    }, [state.AuthStore.Token]);

    function csvError(data: any) {
        console.log(data);
    }

    function uploadCSVData(data: any[], file: any) {
        SetShowIncorrectFileType(false);
        SetShowUploadCSVDataAlert(false);

        if (file.name.split(".").slice(-1)[0].toLowerCase() !== "csv") {
            SetShowIncorrectFileType(true);
            trackException(appInsights, new Error(), SeverityLevel.Warning, "Onboarding", "Upload CSV", "User attempted to upload non-csv file for Onboarding", state.AuthStore, {});
            return;
        }

        SetHideOverLimitMessage(true);
        const loadingServers: IOnboardingDataType[] = [{
            ServerId: "",
            Status: "Loaded",
            ServiceName: "Loading...",
            ServiceTreeGUID: "",
            Server: "",
            Domain: "",
            Notes: "",
            ErrorMessage: "",
            Jobs: [],
            JobStatus: ""
        }];

        SetDataGridData(loadingServers);

        const newServerData: IOnboardingDataType[] = [];

        for (let i = 0; i < data.length; i++) {
            const fqdn: string = data[i].data[0];
            if (fqdn !== undefined && fqdn !== "" && fqdn.toLowerCase() !== "fqdn") {
                const validationResult: IFQDNValidation = validateFQDN(fqdn.toLowerCase());

                if (validationResult.Valid) {
                    newServerData.push({
                        ServerId: i.toString(),
                        Status: "Loaded",
                        ServiceName: "",
                        ServiceTreeGUID: "",
                        Server: validationResult.Server,
                        Domain: validationResult.Domain,
                        Notes: "",
                        ErrorMessage: "",
                        Jobs: [],
                        JobStatus: ""
                    });
                } else {
                    newServerData.push({
                        ServerId: i.toString(),
                        Status: "FAIL",
                        ServiceName: "",
                        ServiceTreeGUID: "",
                        Server: validationResult.Server,
                        Domain: validationResult.Domain,
                        Notes: validationResult.Error,
                        ErrorMessage: "",
                        Jobs: [],
                        JobStatus: ""
                    });
                }
            }
        }

        if (newServerData.length <= maxOnboardingCount) {
            SetDataGridData(newServerData);
            props.SetServerData(newServerData);
            SetHideResults(false);
        } else {
            SetHideOverLimitMessage(false);
            SetHideResults(true);
        }

        SetShowUploadCSVDataAlert(true);
        SetAnnounceCSVUpload(<Announced message="CSV Uploaded" aria-live="assertive" />);
    }

    const validateFQDN = (fqdn: string): IFQDNValidation => {
        let fqdnRegex: RegExp = /[[a-zA-Z0-9!@#\$%\^\&*\)\(+=._-]+[.]+[a-zA-Z0-9]+\.corp\.microsoft\.com/gi;

        // trim FQDN
        fqdn = fqdn.trim();

        if (fqdnRegex.test(fqdn)) {
            return {
                Valid: true,
                Server: fqdn.substring(0, fqdn.indexOf(".")),
                Domain: fqdn.substring(fqdn.indexOf(".") + 1, fqdn.length),
                Error: "",
            };

        } else {
            return {
                Valid: false,
                Server: fqdn,
                Domain: "",
                Error: "FQDN is not valid",
            };
        }
    }

    // File selection dialog
    const buttonRef: React.RefObject<any> = React.createRef();
    const handleOpenDialog = (e: any) => {
        // Note that the ref is set async, so it might be null at some point
        if (buttonRef.current) {
            buttonRef.current.open(e);
        }
    };

    const onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
        const newColumns: IColumn[] = columns.slice();
        const currColumn: IColumn = newColumns.filter((currCol) => column.key === currCol.key)[0];

        newColumns.forEach((newCol: IColumn) => {
            if (newCol === currColumn) {
                const initialSorting: boolean = !currColumn.isSortedDescending;
                SetDescendingColumn(column.key);
                SetColumnIsSortedDescending(!initialSorting);
                SetSortedColumn(column.key);
            }
        });

        const newItems: IOnboardingDataType[] = copyAndSort(dataGridData, currColumn.fieldName!, isSortedDescending(column.key));

        SetDataGridData(newItems);
    }

    const copyAndSort = (items: IOnboardingDataType[], columnKey: string, isSortedDescending?: boolean): IOnboardingDataType[] => {
        const key = columnKey as keyof IOnboardingDataType;
        return items.slice(0).sort((a: IOnboardingDataType, b: IOnboardingDataType) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
    }

    const isSortedDescending = (column: string): boolean => {
        if (column === descendingColumn) {
            return columnIsSortedDescending;
        } else {
            return true;
        }
    }

    const isSorted = (column: string): boolean => {
        return column === sortedColumn;
    }

    const columns: IColumn[] = [
        {
            key: "status",
            name: "Status",
            fieldName: "Status",
            minWidth: 70,
            maxWidth: 100,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("status") ? false : true,
            isSorted: isSorted("status"),
        },
        {
            key: "server",
            name: "Server",
            fieldName: "Server",
            minWidth: 100,
            maxWidth: 140,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("server") ? false : true,
            isSorted: isSorted("server"),
        },
        {
            key: "domain",
            name: "Domain",
            fieldName: "Domain",
            minWidth: 150,
            maxWidth: 160,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("domain") ? false : true,
            isSorted: isSorted("domain"),
        },
        {
            key: "notes",
            name: "Notes",
            fieldName: "Notes",
            minWidth: 70,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("notes") ? false : true,
            isSorted: isSorted("notes"),
            onRender(item?: IOnboardingDataType) {
                return <span className={breakWord}>{renderNotesWithLinks(item?.Notes)}</span>;
            },
        },
        ...(state.AuthStore.isAdmin && state.AuthStore.toggleAdmin ? [{
            key: "errorMessage",
            name: "Error Message",
            fieldName: "ErrorMessage",
            minWidth: 70,
            maxWidth: 200,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("errorMessage") ? false : true,
            isSorted: isSorted("errorMessage"),
            onRender(item?: IOnboardingDataType) {
                return <span className={breakWord}>{item?.ErrorMessage || ""}</span>;
            },
        }] : []),
        {
            key: "jobStatus",
            name: "Jobs",
            fieldName: "JobStatus",
            minWidth: 70,
            maxWidth: 125,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("JobStatus") ? false : true,
            isSorted: isSorted("JobStatus"),
            onRender(item?: IOnboardingDataType, index?, column?) {
    if (item?.JobStatus !== undefined && item?.JobStatus !== "") {
        const newServer: AddServersToJobsPanelType = {
            ServerId: item?.ServerId!,
            ServerName: item?.Server!,
            ServiceName: item?.ServiceName!,
            ServiceTreeGUID: item?.ServiceTreeGUID!,
        }
        const addServersToJobsPanelProps: AddServersToJobsPanelProps = {
            newServer: newServer,
            buttonText: item?.JobStatus!
        }
        return <AddServersToJobsPanel {...addServersToJobsPanelProps} />;
    } else {
        return <span>{"N/A"}</span>
    }
},
        },
    ];

    return (
        <div className={mergeStyles({ maxWidth: "800px", width: "100%" })}>
            <h2>Upload Server Data</h2>
            <ol>
                <li>Download pre-formated <CSVLink aria-label={"Click here to download " + csvFileName} filename={csvFileName} title={"Click here to download " + csvFileName} data={csvData} headers={csvHeaders} style={{ textDecoration: 'underline' }}>{csvFileName}</CSVLink> file.</li>
                <li className={breakWord}>Fill out each row with full FQDN (i.e. SERVER.DOMAIN.corp.microsoft.com).
                    <ul><li><b>Please limit data to {maxOnboardingCount} entries or fewer.</b></li></ul>
                    <ul><li>For <b>Linux</b> devices, If your device is not yet registered to any domain, please follow <Link href="https://eng.ms/docs/office-of-coo/microsoft-digital/tenant-integration-and-mgmt/disruption-free-updates/patching-service/md-patching-service/mdpatchingportal/prereqlinux" target="_blank" rel="noopener noreferrer" styles={{ root: { textDecoration: 'underline' } }}>these instructions</Link>.</li></ul>
                </li>
                <li>Upload completed CSV file</li>
            </ol>
            <br />
            <CSVReader
                ref={buttonRef}
                onFileLoad={uploadCSVData}
                onError={csvError}
                noClick
                noDrag
            >
                {({ file }: any) => (
                    <aside
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            marginBottom: 10,
                        }}
                    >
                        <TooltipHost
                            content="Browse"
                            id={tooltipId}
                            calloutProps={calloutProps}
                        >
                            <Button
                                appearance="primary"
                                onClick={handleOpenDialog}
                                aria-label="Click browse button to uplaod a CSV file"
                                role={"button"}
                                id={"browseButton"}
                            >
                                Browse
                            </Button>
                        </TooltipHost>
                        <div
                            style={{
                                borderWidth: 1,
                                borderStyle: "solid",
                                borderColor: "#ccc",
                                lineHeight: 2,
                                marginTop: 0,
                                marginBottom: 0,
                                paddingLeft: 13,
                                paddingTop: 0,
                                width: "60%",
                            }}
                        >
                            {file && file.name}
                        </div>
                    </aside>
                )}
            </CSVReader>
            {state.AuthStore.isAdmin && state.AuthStore.toggleAdmin &&
                <>
                    <br />
                    <Checkbox
                        id="overrideServersOUCheckBox"
                        ariaLabel="Please check to confirm you want to override service tree to Machines Servers OU"
                        boxSide="start"
                        checked={overrideServersOU}
                        onChange={(ev, isChecked) => {
                            SetOverrideServersOU(!!isChecked);
                            props.SetOverwriteServicetree(!!isChecked);
                        }}
                        className={mergeStyles({ marginTop: "10px" })}
                        onRenderLabel={() => (
                            <span style={{ marginLeft: 4 }}>
                                Please check to confirm you want to override service tree to <b>Machines Servers OU</b>
                            </span>
                        )}
                    />
                </>
            }
            <div>
                {announceCSVUpload}
                <p role={"alert"} aria-live="assertive" >{showUploadCSVDataAlert ? "CSV successfully uploaded" : ""}</p>
            </div>
            <br />
            <div hidden={hideOverLimitMessage}>
                <b className={breakWord}>You have attempted to onboard more than {maxOnboardingCount} servers. Please re-upload CSV with fewer than {maxOnboardingCount}.</b>
            </div>
            <div hidden={!showIncorrectFileTypeMessage}>
                <b className={breakWord}>Only CSV files are acceptable.</b>
            </div>
            <div hidden={hideResults}>
                <div className="text-center">
                    <TooltipHost
                        content="Onboard Assets"
                        id={tooltipId}
                        calloutProps={calloutProps}
                    >
                        <Button
                            role="button"
                            appearance="primary"
                            aria-label="Onboard Assets"
                            onClick={() => props.SetCurrentStep(OnboardingStep.Onboard)}
                        >Onboard Assets</Button>
                    </TooltipHost>
                </div>
                <br />
                <div style={{ position: "relative", height: "50vh", maxWidth: "60vw", width: "100%" }}>
                    <ScrollablePane style={{ height: "50vh", maxWidth: "60vw", width: "100%", position: "relative" }}>
                        <DetailsList
                            columns={columns}
                            items={dataGridData}
                            compact={true}
                            onRenderDetailsHeader={RenderStickyHeader}
                            checkboxVisibility={CheckboxVisibility.hidden}
                        />
                    </ScrollablePane>
                </div>
            </div>
        </div>
    );
};

export const OnboardComponent: React.FunctionComponent<IOnboardComponentProps> = (props: IOnboardComponentProps) => {
    const [onboardingResultsData, SetOnboardingResultsData] = React.useState<IOnboardingDataType[]>([]);
    const { state, dispatch } = React.useContext(RootContext); // auth token context
    const [onboardingStatus, SetOnboardingStatus] = React.useState<string>("Onboarding your servers...");
    const [successfulServersOnboarded, SetSuccessfulServersOnboarded] = React.useState<number>(0);
    const [showResults, SetShowResults] = React.useState<boolean>(false);
    const [hideErrorMessage, SetHideErrorMessage] = React.useState<boolean>(true);
    const [hidePermissionMessage, SetHidePermissionMessage] = React.useState<boolean>(true);
    const [columnIsSortedDescending, SetColumnIsSortedDescending] = React.useState<boolean>(false);
    const [descendingColumn, SetDescendingColumn] = useState<string>("Status");
    const [sortedColumn, SetSortedColumn] = useState<string>("Status");

    const appInsights = useAppInsights();

    // HttpService
    const [httpService] = React.useState(HttpService(appInsights, state));

    const csvFileName = "onboarding_results";

    const tooltipId = useId("tooltip");

    React.useEffect(() => {
        if (props.serverData.length > 0) {
            const addOnboardingData = (serverFQDNs: string[], badFQDNs: IOnboardingDataType[]) => {
                httpService.post({
                    url: "api/ServerOnboarding/addServers",
                    token: state.AuthStore.Token,
                    data: {
                        FQDNs: serverFQDNs,
                        OverwriteServicetree: props.overwriteServicetree ?? false,
                    },
                })
                    .then((resp: any) => {
                        try {
                            console.log("Onboarding API response:", resp);
                            const data = resp?.data;
                            if (resp?.status.toString() === "200" && data !== UnauthorizedMessage) {
                                let results: IOnboardingDataType[] = [];
                                const newServices: IServiceDataType[] = [];

                                if (data && Array.isArray(data)) {
                                    for (const d of data) {
                                        let serverStatus: any = "Success";

                                        const remarks = d.Remarks ?? "";
                                        const successRemarks = ["", "Success", "Server already Onboarded in Patching Portal"];
                                        if (!successRemarks.includes(remarks)) {
                                            serverStatus = "FAIL";
                                        }

                                        const jobData: IJobInfo[] = [];

                                        if (d.Jobs && Array.isArray(d.Jobs)) {
                                            d.Jobs.forEach((job: any) => {
                                                jobData.push({
                                                    jobId: job.JobId,
                                                    jobName: job.JobName,
                                                });
                                            });
                                        }

                                        const fqdn: string = d.FQDN || "";
                                        const dotIndex = fqdn.indexOf(".");
                                        const serverName: string = dotIndex > -1 ? fqdn.substring(0, dotIndex) : fqdn;
                                        const domain: string = dotIndex > -1 ? fqdn.substring(dotIndex + 1) : "";

                                        const emptyGuid = "00000000-0000-0000-0000-000000000000";
                                        const hasValidService = (d.ServiceTreeGUID ?? "") !== "" && (d.ServiceTreeGUID ?? "") !== emptyGuid;

                                        const getJobStatus = (): string => {
                                            if (serverStatus === "FAIL") return "";
                                            if (jobData.length > 0) return "Jobs (" + jobData.length.toString() + ")";
                                            if (hasValidService) return "Add To Job";
                                            return "";
                                        };

                                        const jobStatusValue = getJobStatus();

                                        if (hasValidService) {
                                            newServices.push({
                                                ServiceName: d.ServiceName ?? "",
                                                ServiceTreeGUID: d.ServiceTreeGUID ?? "",
                                            });
                                        }

                                        results = [...results, {
                                            ServerId: d.ServerId ?? "",
                                            Status: serverStatus,
                                            ServiceName: d.ServiceName ?? "",
                                            ServiceTreeGUID: d.ServiceTreeGUID ?? "",
                                            Server: serverName,
                                            Domain: domain,
                                            Notes: remarks,
                                            ErrorMessage: d.ErrorMessage ?? "",
                                            Jobs: jobData,
                                            JobStatus: jobStatusValue,
                                        }];
                                    }
                                } else {
                                    console.error("Onboarding response data is not an array:", data);
                                }

                                // add in servers that failed FQDN validation
                                // and therefore were not sent to onboarding
                                for (const d of badFQDNs) {
                                    results = [...results, d];
                                }

                                try {
                                    updateServicesContext(newServices);
                                } catch (e) {
                                    console.error("updateServicesContext error:", e);
                                }
                                updateResults(results);
                            } else {
                                console.error("Onboarding failed - status:", resp?.status, "data:", data);
                                updateResults([{
                                    ServerId: "",
                                    Status: "FAIL",
                                    ServiceName: "",
                                    ServiceTreeGUID: "",
                                    Server: "",
                                    Domain: "",
                                    Notes: UnauthorizedMessage,
                                    ErrorMessage: "",
                                    Jobs: [],
                                    JobStatus: "",
                                }]);
                                SetHidePermissionMessage(false);
                                trackException(appInsights, new Error(), SeverityLevel.Warning, "Onboarding", "addOnboardingdata", "response status code: " + resp.status.toString(), state.AuthStore, {});
                            }
                        } catch (parseError: any) {
                            console.error("Error parsing onboarding response:", parseError);
                            console.error("Raw response was:", resp);
                            console.error("Stack trace:", parseError?.stack);
                            trackException(appInsights, parseError, SeverityLevel.Error, "Onboarding", "Onboard", "Error parsing addServers response", state.AuthStore, { rawResponse: JSON.stringify(resp?.data) });
                            updateResults([{
                                ServerId: "",
                                Status: "FAIL",
                                ServiceName: "",
                                ServiceTreeGUID: "",
                                Server: "",
                                Domain: "",
                                Notes: "An error occurred while processing the onboarding results. Check console for details.",
                                ErrorMessage: parseError?.message ?? "",
                                Jobs: [],
                                JobStatus: "",
                            }]);
                            SetHideErrorMessage(false);
                        }
                    }).catch((reason: any) => {
                        console.error("Onboarding API call failed:", reason);
                        console.error("Stack trace:", reason?.stack);
                        updateResults([{
                            ServerId: "",
                            Status: "FAIL",
                            ServiceName: "",
                            ServiceTreeGUID: "",
                            Server: "",
                            Domain: "",
                            Notes: UnauthorizedMessage,
                            ErrorMessage: "",
                            Jobs: [],
                            JobStatus: "",
                        }]);
                        SetHideErrorMessage(false);
                        trackException(appInsights, reason, SeverityLevel.Error, "Onboarding", "Onboard", "addServers", state.AuthStore, {});
                    });
            };

            let FQDNArray: string[] = [];
            let badFQDNs: IOnboardingDataType[] = [];
            for (let i = 0; i < props.serverData.length; i++) {
                if (props.serverData[i]?.Status !== "FAIL") {
                    const FQDN: string = props.serverData[i]?.Server + "." + props.serverData[i].Domain;
                    FQDNArray = [...FQDNArray, FQDN];
                } else {
                    badFQDNs = [...badFQDNs, props.serverData[i]];
                }
            }

            addOnboardingData(FQDNArray, badFQDNs);
        } else {
            // no servers to be uploaded - csv is blank
            updateResults([{
                ServerId: "",
                Status: "FAIL",
                ServiceName: "",
                ServiceTreeGUID: "",
                Server: "",
                Domain: "",
                Notes: UnauthorizedMessage,
                ErrorMessage: "",
                Jobs: [],
                JobStatus: "",
            }]);
            SetHidePermissionMessage(false);
            trackException(appInsights, new Error(), SeverityLevel.Warning, "Onboarding", "Onboard", "CSV is blank", state.AuthStore, {});
            SetOnboardingStatus("No Servers Onboarded. Please Try Again.");
        }
    }, [props.serverData, props.overwriteServicetree]);

    const updateServicesContext = (services: IServiceDataType[]) => {
        try {
            const onboardedServices = state.DataStore.OnboardedServices;
            if (!onboardedServices || onboardedServices.length == 0) { // query all services and add new ones
                getOnboardedServices(state, appInsights).then((response: any) => {
                    const data: any = response?.data;
                    if (data !== "" && data !== undefined && Array.isArray(data)) {
                        const onboardedServicesState: IServiceDataType[] = [];
                        const servicesSet: Set<string> = new Set<string>();

                        for (const { serviceName, serviceTreeGUID } of data) {
                            if (serviceTreeGUID && !onboardedServicesState.some(s => s.ServiceTreeGUID === serviceTreeGUID)) {
                                onboardedServicesState.push({ ServiceName: serviceName, ServiceTreeGUID: serviceTreeGUID });
                                servicesSet.add(serviceTreeGUID);
                            }
                        }

                        // add new services
                        const emptyGuid = "00000000-0000-0000-0000-000000000000";
    for (const { ServiceName, ServiceTreeGUID } of services.filter(s => s.ServiceTreeGUID && s.ServiceTreeGUID !== "" && s.ServiceTreeGUID !== emptyGuid && !servicesSet.has(s.ServiceTreeGUID))) {
        onboardedServicesState.push({ ServiceName: ServiceName, ServiceTreeGUID: ServiceTreeGUID });
        servicesSet.add(ServiceTreeGUID);
    }

                        dispatch({
                            type: DataActionEnum.UPDATE_ONBOARDED_SERVICES,
                            onboardedServices: onboardedServicesState,
                        });
                    }
                }).catch((reason: any) => {
                    trackException(appInsights, reason, SeverityLevel.Error, "Manage Assets", "Fetch Page Data", "api/ServiceAdministration/fetchOnboardedServices", state.AuthStore, {});
                });
            } else { // add new services to current context
                const onboardedServicesState: IServiceDataType[] = [...onboardedServices];
                const emptyGuid = "00000000-0000-0000-0000-000000000000";
                const newServices: IServiceDataType[] = services.filter(s => s.ServiceName !== "" && s.ServiceName !== null && s.ServiceName !== undefined && s.ServiceTreeGUID !== emptyGuid);

                const servicesSet: Set<string> = new Set<string>(onboardedServicesState.map(s => s.ServiceTreeGUID));

                // add in new services
                for (const s of newServices) {
                    if (!servicesSet.has(s.ServiceTreeGUID) && s.ServiceTreeGUID !== "" && s.ServiceTreeGUID !== null && s.ServiceTreeGUID !== undefined) {
                        onboardedServicesState.push({
                            ServiceName: s.ServiceName,
                            ServiceTreeGUID: s.ServiceTreeGUID,
                        });

                        servicesSet.add(s.ServiceTreeGUID);
                    }
                }

                dispatch({
                    type: DataActionEnum.UPDATE_ONBOARDED_SERVICES,
                    onboardedServices: onboardedServicesState,
                });
            }
        } catch (e) {
            console.error("updateServicesContext error:", e);
        }
    }

    const updateResults = (allServers: IOnboardingDataType[]) => {
        SetOnboardingStatus(onboardingStatusText);

        SetOnboardingResultsData(allServers);

        // count successfully onboarded servers
        SetSuccessfulServersOnboarded(allServers.filter((s) => s.Status === "Success").length);

        try {
            const resultSummary = allServers.map(s => `${s.Server}.${s.Domain}:${s.Status}`).join(",");
            trackEvent(appInsights, "Onboarding", "Onboarding Results", resultSummary, state.AuthStore, {});
        } catch (e) {
            console.error("trackEvent error:", e);
        }
        SetShowResults(true);
    };

    const onColumnClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
        const newColumns: IColumn[] = serversColumns.slice();
        const currColumn: IColumn = newColumns.filter((currCol) => column.key === currCol.key)[0];

        newColumns.forEach((newCol: IColumn) => {
            if (newCol === currColumn) {
                const initialSorting: boolean = !currColumn.isSortedDescending;
                SetDescendingColumn(column.key);
                SetColumnIsSortedDescending(!initialSorting);
                SetSortedColumn(column.key);
            }
        });

        const newItems: IOnboardingDataType[] = copyAndSort(onboardingResultsData, currColumn.key!, isSortedDescending(column.key));

        SetOnboardingResultsData(newItems);
    }

    const copyAndSort = (items: IOnboardingDataType[], columnKey: string, isSortedDescending?: boolean): IOnboardingDataType[] => {
        const key = columnKey as keyof IOnboardingDataType;
        return items.slice(0).sort((a: IOnboardingDataType, b: IOnboardingDataType) => ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1));
    }

    const isSortedDescending = (column: string): boolean => {
        if (column === descendingColumn) {
            return columnIsSortedDescending;
        } else {
            return true;
        }
    }

    const isSorted = (column: string): boolean => {
        return column === sortedColumn;
    }

    const serversColumns: IColumn[] = [
        {
            key: "status",
            name: "Status",
            fieldName: "Status",
            minWidth: 70,
            maxWidth: 70,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("status") ? false : true,
            isSorted: isSorted("status"),
        },
        {
            key: "serviceName",
            name: "ServiceName",
            fieldName: "ServiceName",
            minWidth: 70,
            maxWidth: 125,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("service") ? false : true,
            isSorted: isSorted("service"),
        },
        {
            key: "server",
            name: "Server",
            fieldName: "Server",
            minWidth: 70,
            maxWidth: 125,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("server") ? false : true,
            isSorted: isSorted("server"),
        },
        {
            key: "domain",
            name: "Domain",
            fieldName: "Domain",
            minWidth: 70,
            maxWidth: 170,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("domain") ? false : true,
            isSorted: isSorted("domain"),
        },
        {
            key: "notes",
            name: "Notes",
            fieldName: "Notes",
            minWidth: 70,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("notes") ? false : true,
            isSorted: isSorted("notes"),
            onRender(item?: IOnboardingDataType) {
                return <span className={breakWord}>{renderNotesWithLinks(item?.Notes)}</span>;
            },
        },
        ...(state.AuthStore.isAdmin && state.AuthStore.toggleAdmin ? [{
            key: "errorMessage",
            name: "Error Message",
            fieldName: "ErrorMessage",
            minWidth: 70,
            maxWidth: 200,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("errorMessage") ? false : true,
            isSorted: isSorted("errorMessage"),
            onRender(item?: IOnboardingDataType) {
                return <span className={breakWord}>{item?.ErrorMessage || ""}</span>;
            },
        }] : []),
        {
            key: "jobStatus",
            name: "Jobs",
            fieldName: "JobStatus",
            minWidth: 70,
            maxWidth: 125,
            isResizable: true,
            isMultiline: true,
            onColumnClick: onColumnClick,
            isSortedDescending: isSortedDescending("JobStatus") ? false : true,
            isSorted: isSorted("JobStatus"),
            onRender(item?: IOnboardingDataType, index?, column?) {
    if (item?.JobStatus !== undefined && item?.JobStatus !== "") {
        const newServer: AddServersToJobsPanelType = {
            ServerId: item?.ServerId!,
            ServerName: item?.Server!,
            ServiceName: item?.ServiceName!,
            ServiceTreeGUID: item?.ServiceTreeGUID!,
        }
        const addServersToJobsPanelProps: AddServersToJobsPanelProps = {
            newServer: newServer,
            buttonText: item?.JobStatus!
        }
        return <AddServersToJobsPanel {...addServersToJobsPanelProps} />;
    } else {
        return <span>{"N/A"}</span>
    }
},
        },
    ];

    const onRenderRow = (onRenderProps: any, defaultRender?: any) => {
        return (
            <div>
                <TooltipHost
                    content={onRenderProps?.item?.Notes}
                    calloutProps={calloutProps}
                    styles={hostStyles}
                >
                    {defaultRender!(onRenderProps)}
                </TooltipHost>
            </div>
        );
    };

    // Flatten data for CSVLink export (remove complex nested objects)
    const csvExportData = React.useMemo(() => {
        return onboardingResultsData.map(item => ({
            Status: item.Status,
            ServiceName: item.ServiceName,
            Server: item.Server,
            Domain: item.Domain,
            Notes: item.Notes,
            ...(state.AuthStore.isAdmin && state.AuthStore.toggleAdmin ? { ErrorMessage: item.ErrorMessage } : {}),
            JobStatus: item.JobStatus,
        }));
    }, [onboardingResultsData, state.AuthStore.isAdmin, state.AuthStore.toggleAdmin]);

    return (
        <div>
            <h2>{onboardingStatus}</h2>
            <div hidden={showResults}>
                <ProgressIndicator label={onboardingStatus} description="Please wait..." />
            </div>
            {/* show results of onboarding after complete */}
            <div hidden={!showResults}>
                <div hidden={hidePermissionMessage}>
                    <b>Onboarding Failed. You do not have permission to onboard devices for this service. Please visit the home page to request access.</b>
                </div>
                <div hidden={hideErrorMessage}>
                    <b>Onboarding Failed. Please contact administrator.</b>
                </div>
                <div hidden={!hidePermissionMessage || !hideErrorMessage}>
                    <div>
                        {successfulServersOnboarded} / {props.serverData.length} server(s) successfully onboarded.
                        <br />
                        <div>
                            <CSVLink
                                aria-label={"Export Results"}
                                filename={csvFileName}
                                title={"Export Results"}
                                data={csvExportData}
                                style={{ textDecoration: 'underline' }}>
                                Export Results
                            </CSVLink>
                        </div>
                        <br />

                        If any of your devices failed to onboard, please visit this link to review the FAQ:
                        <br />
                        <Link
                               href="https://eng.ms/docs/engineering-enablement-and-operations/microsoft-digital/tenant-integration-and-mgmt/disruption-free-updates/patching-service/md-patching-service/#prerequisites-to-onboard"
                            target="_blank"
                            rel="noopener noreferrer"
                            styles={{ root: { textDecoration: 'underline' } }}>
                            Onboarding Errors FAQ
                        </Link>
                    </div>
                    <ScrollablePane style={{ height: "45vh", maxWidth: "60vw", width: "100%", position: "relative" }}>
                        <DetailsList
                            columns={serversColumns}
                            items={onboardingResultsData}
                            compact={true}
                            onRenderDetailsHeader={RenderStickyHeader}
                            checkboxVisibility={CheckboxVisibility.hidden}
                            onRenderRow={onRenderRow}
                        />
                    </ScrollablePane>
                    <br />
                    <br />
                    <Link role="link" ariaLabel={"Link to onboard more servers"}
                        styles={{ root: { textDecoration: 'underline' } }}
                        onClick={() => {
                            window.location.reload();
                        }}
                    >
                        Click here to onboard more servers
                    </Link>
                </div>
            </div>
        </div>
    );
};

const renderNotesWithLinks = (notes?: string) => {
    if (!notes) {
        return null;
    }

    const regex = /(https?:\/\/[^\s]+)/g;
    const parts = notes.split(regex);

    return (
        <>
            {parts.map((part, index) => {
                if (part.match(regex)) {
                    return (
                        <a key={index} href={part} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>
                            {part}
                        </a>
                    );
                }

                return part;
            })}
        </>
    );
};
